import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Form, Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  MdSend,
  MdEmail,
  MdPerson,
  MdSubject,
  MdMessage,
  MdLink,
} from "react-icons/md";
import "./EmailModal.css";
import { sendEmailRequest } from "../../redux/actions/oemActions";

const EmailModal = ({ isOpen, onClose, oemId, shareUrl ,shareLicenseKey}) => {
  const dispatch = useDispatch();
  const { emailLoading, emailSuccess } = useSelector((state) => state.oem);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      to: "",
      cc: "",
      subject: "Your Cyber Risk Assessment Link is Ready",
      body: "We have prepared a personalised Cyber Risk Assessment for your domain. Please click the button below to access your secure scanning portal and generate your report.",
    },
  });

  const watchedTo = watch("to");
  const watchedBody = watch("body");
  const wordCount = watchedBody ? watchedBody.trim().split(/\s+/).filter(word => word.length > 0).length : 0;

  const validateEmails = (emailString) => {
    if (!emailString) return true;
    const emails = emailString.split(",").map((email) => email.trim());
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emails.every((email) => emailRegex.test(email)) || "Please enter valid email addresses";
  };

  const onSubmit = (data) => {
    const emailData = {
      to: data.to,
      cc: data.cc ? data.cc.split(",").map((email) => email.trim()) : [],
      oemId,
      subject: data.subject,
      body: data.body,
    };
    dispatch(sendEmailRequest(emailData));
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (emailSuccess) {
      handleClose();
    }
  }, [emailSuccess]);

  return (
    <Modal
      show={isOpen}
      onHide={handleClose}
      centered
      size="lg"
      className="email-modal-bootstrap"
      backdrop="static"
    >
      <Modal.Header className="email-modal-header">
        <div className="header-content">
          <div className="email-icon">
            <MdEmail />
          </div>
          <div className="header-text">
            <Modal.Title as="h3">Send Security Report</Modal.Title>
            <p>Share domain scanning link via email</p>
          </div>
        </div>
      </Modal.Header>

      {/* ✅ Form now wraps Body + Footer */}
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body className="email-modal-body">
          <Form.Group className="mb-3">
            <Form.Label className="custom-label">
              <MdPerson className="label-icon" />
              Recipient Email <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="email"
              {...register("to", {
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Please enter a valid email address",
                },
              })}
              placeholder="Enter recipient email address"
              className={`custom-input ${errors.to ? "is-invalid" : ""}`}
            />
            {errors.to && (
              <div className="invalid-feedback">{errors.to.message}</div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="custom-label">
              <MdPerson className="label-icon" />
              CC (Optional)
            </Form.Label>
            <Form.Control
              type="text"
              {...register("cc", {
                validate: {
                  emails: validateEmails,
                  notOnlySpaces: (value) => !value || value.trim() !== "" || "This field is required",
                  notSameAsRecipient: (value) => {
                    if (!value || !watchedTo) return true;
                    const ccEmails = value.split(",").map(email => email.trim().toLowerCase());
                    const recipientEmail = watchedTo.trim().toLowerCase();
                    return !ccEmails.includes(recipientEmail) || "CC cannot contain the same email as recipient";
                  }
                }
              })}
              placeholder="Additional recipients (comma separated)"
              className={`custom-input ${errors.cc ? "is-invalid" : ""}`}
            />
            {errors.cc && (
              <div className="invalid-feedback">{errors.cc.message}</div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="custom-label">
              <MdSubject className="label-icon" />
              Subject <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              {...register("subject", { 
                required: "Subject is required",
                validate: (value) => value.trim() !== "" || "This field is required"
              })}
              className={`custom-input ${errors.subject ? "is-invalid" : ""}`}
            />
            {errors.subject && (
              <div className="invalid-feedback">{errors.subject.message}</div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="custom-label d-flex justify-content-between align-items-center">
              <span>
                <MdMessage className="label-icon me-2" />
                Message <span className="text-danger">*</span>
              </span>
              <small className={`${wordCount > 50 ? 'text-danger' : 'text-muted'}`}>
                {wordCount}/50 words
              </small>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              {...register("body", { 
                required: "Message is required",
                validate: {
                  notEmpty: (value) => value.trim() !== "" || "This field is required",
                  wordLimit: (value) => {
                    const wordCount = value.trim().split(/\s+/).filter(word => word.length > 0).length;
                    return wordCount <= 50 || "Message cannot exceed 50 words";
                  }
                }
              })}
              onKeyDown={(e) => {
                const currentWordCount = e.target.value.trim().split(/\s+/).filter(word => word.length > 0).length;
                if (currentWordCount >= 50 && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
                  e.preventDefault();
                }
              }}
              placeholder="Enter your message here..."
              className={`custom-input ${errors.body ? "is-invalid" : ""}`}
            />
            {errors.body && (
              <div className="invalid-feedback">{errors.body.message}</div>
            )}
          </Form.Group>

          <div className="url-preview">
            <div className="url-header">
              <MdLink />
              <span>Scan URL to be shared</span>
            </div>
            <div className="url-content">
              <code>{shareUrl}</code>
              {shareLicenseKey && (
                <div className="license-key">
                  <p className="mb-0" style={{fontSize:"12px"}}>License Key: <span className="text-primary">{shareLicenseKey}</span></p>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="email-modal-footer">
          <Button
            variant="secondary"
            onClick={handleClose}
            className="cancel-btns"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={emailLoading}
            className="send-btn"
          >
            {emailLoading ? (
              <>
                <div className="spinneremail"></div>
                Sending...
              </>
            ) : (
              <>
                <MdSend />
                Send Email
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EmailModal;
